'use client'

/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useEffect, useState } from 'react'
import type { ReactQuillProps } from 'react-quill'
import 'katex'

import 'react-quill/dist/quill.snow.css'

type ReactQuillModule = typeof import('react-quill')

const QuillWrapper = forwardRef<any, ReactQuillProps>((props, ref) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const [ReactQuillComp, setReactQuillComp] = useState<ReactQuillModule['default'] | null>(null)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const ReactQuillMod = await import('react-quill')
      const Quill = (await import('quill')).default

      ;(globalThis as any).Quill = Quill

      // ----- Hashtag Inline Blot -----
      const Inline = Quill.import('blots/inline') as any
      class HashtagBlot extends Inline {
        static blotName = 'hashtag'
        static tagName = 'span'
        static className = 'ql-hashtag'
        static create(value: string) {
          const node = super.create() as HTMLElement
          node.setAttribute('data-hashtag', value)
          node.classList.add('hashtag', 'bg-blue-100', 'text-blue-800', 'rounded', 'px-1', 'py-0.5')
          node.style.whiteSpace = 'nowrap'
          node.style.display = 'inline-block'
          node.style.userSelect = 'none'
          node.style.cursor = 'pointer'
          node.style.margin = '0 1px'
          return node
        }
        static formats(node: HTMLElement) {
          return node.getAttribute('data-hashtag')
        }
        static value(node: HTMLElement) {
          return node.getAttribute('data-hashtag')
        }
        optimize(context: any) {
          super.optimize(context)
          // Ensure hashtag styling is maintained
          if (this.domNode && this.domNode.parentNode) {
            this.domNode.style.whiteSpace = 'nowrap'
            this.domNode.style.display = 'inline-block'
          }
        }
      }
      Quill.register('formats/hashtag', HashtagBlot, true)

      const getCursorPosAfterMention = (quill: any, mentionIndex: number) => {
        const afterEmbed = mentionIndex + 1
        if (afterEmbed < quill.getLength() && quill.getText(afterEmbed, 1) === ' ') {
          return afterEmbed + 1
        }
        return afterEmbed
      }

      const setMentionAdjacentSelection = (
        quill: any,
        node: HTMLElement,
        side: 'before' | 'after'
      ) => {
        try {
          const blot = Quill.find(node, true)
          if (!blot) return

          const index = quill.getIndex(blot)
          const pos = side === 'after' ? getCursorPosAfterMention(quill, index) : index

          quill.focus()
          requestAnimationFrame(() => {
            quill.setSelection(pos, 0, 'user')
            quill.focus()
          })
        } catch {
          // Ignore selection errors when the editor is not ready.
        }
      }

      // ----- Mention Embed Blot -----
      const Embed = Quill.import('blots/embed') as any
      class MentionBlot extends Embed {
        static blotName = 'mention'
        static tagName = 'a'
        static className = 'ql-mention'

        static create(value: { id: string; displayName: string; text: string; profileId?: string }) {
          const node = super.create() as HTMLAnchorElement
          const profileId = value.profileId || value.id
          node.setAttribute('href', `/user/${profileId}`)
          node.setAttribute('data-mention-id', profileId)
          node.setAttribute('data-profile-id', profileId)
          node.setAttribute('contenteditable', 'false')
          node.classList.add('mention', 'font-bold', 'text-blue-700', 'hover:underline')
          node.style.whiteSpace = 'nowrap'
          node.style.display = 'inline'
          node.style.userSelect = 'none'
          node.style.cursor = 'pointer'
          node.style.textDecoration = 'none'
          node.style.background = 'transparent'
          node.textContent = value.text || `@${value.displayName}`

          node.addEventListener('mousedown', (e: MouseEvent) => {
            const editorContainer = node.closest('.ql-container')
            const quillInstance = editorContainer ? (editorContainer as any).__quill : null
            if (!quillInstance) return

            const rect = node.getBoundingClientRect()
            const ratio = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5
            const distFromRight = rect.right - e.clientX
            const isTailClick = distFromRight <= 8 || ratio > 0.6
            const isHeadClick = e.clientX - rect.left <= 8 || ratio < 0.4

            if (isTailClick) {
              e.stopPropagation()
              setMentionAdjacentSelection(quillInstance, node, 'after')
              return
            }

            if (isHeadClick) {
              e.stopPropagation()
              setMentionAdjacentSelection(quillInstance, node, 'before')
              return
            }

            e.stopPropagation()
          })

          node.addEventListener('click', (e: MouseEvent) => {
            const rect = node.getBoundingClientRect()
            const ratio = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5
            const distFromRight = rect.right - e.clientX

            // Tail/head clicks are for cursor placement only — skip profile navigation.
            if (distFromRight <= 8 || ratio > 0.6 || e.clientX - rect.left <= 8 || ratio < 0.4) {
              e.preventDefault()
              e.stopPropagation()
              return
            }

            e.stopPropagation()
          })

          return node
        }

        static value(node: HTMLElement) {
          const href = node.getAttribute('href') || ''
          const profileIdFromHref = href.match(/\/user\/(.+)$/)?.[1] || ''
          return {
            id: node.getAttribute('data-mention-id') || profileIdFromHref || '',
            displayName: node.textContent?.replace('@', '') || '',
            text: node.textContent || '',
            profileId: node.getAttribute('data-profile-id') || profileIdFromHref || '',
          }
        }

        static formats() {
          return true
        }

        optimize(context: any) {
          super.optimize(context)
          if (this.domNode && this.domNode.parentNode) {
            this.domNode.style.whiteSpace = 'nowrap'
            this.domNode.style.display = 'inline'
            this.domNode.style.userSelect = 'none'
          }
        }
      }

      Quill.register('formats/mention', MentionBlot, true)

      // ----- Mention Module -----
      class MentionModule {
        constructor(private quill: any) {
          this.quill = quill
          this.setupKeyboardNavigation()
          this.setupClipboardMatcher()
        }

        private setupClipboardMatcher() {
          const clipboard = this.quill.getModule('clipboard')
          if (!clipboard) return

          const Delta = Quill.import('delta')

          const toMentionDelta = (node: HTMLElement) => {
            const href = node.getAttribute('href') || ''
            const profileIdFromHref = href.match(/\/user\/(.+)$/)?.[1] || ''
            const profileId =
              node.getAttribute('data-profile-id') ||
              node.getAttribute('data-mention-id') ||
              profileIdFromHref
            const text = (node.textContent || '').trim()
            const displayName = text.replace(/^@/, '')

            return new Delta().insert({
              mention: {
                id: profileId,
                displayName,
                text: text.startsWith('@') ? text : `@${displayName}`,
                profileId,
              },
            })
          }

          const matcher = (node: Node) => {
            if (!(node instanceof HTMLElement)) return new Delta()
            return toMentionDelta(node)
          }

          clipboard.addMatcher('A.ql-mention', matcher)
          clipboard.addMatcher('A.mention', matcher)
          clipboard.addMatcher('SPAN.ql-mention', matcher)
          clipboard.addMatcher('SPAN.mention', matcher)
        }

        private setupKeyboardNavigation() {
          this.quill.keyboard.addBinding({
            key: 'ArrowRight',
            handler: (range: { index: number }) => {
              const [leaf] = this.quill.getLeaf(range.index)
              if (leaf?.statics?.blotName === 'mention') {
                const mentionIndex = this.quill.getIndex(leaf)
                this.quill.setSelection(getCursorPosAfterMention(this.quill, mentionIndex), 0, 'user')
                return false
              }
              return true
            },
          })

          this.quill.keyboard.addBinding({
            key: 'ArrowLeft',
            handler: (range: { index: number }) => {
              if (range.index === 0) return true

              const [leaf] = this.quill.getLeaf(range.index)
              if (leaf?.statics?.blotName === 'mention') {
                this.quill.setSelection(this.quill.getIndex(leaf), 0, 'user')
                return false
              }

              const [prevLeaf] = this.quill.getLeaf(range.index - 1)
              if (prevLeaf?.statics?.blotName === 'mention') {
                this.quill.setSelection(this.quill.getIndex(prevLeaf), 0, 'user')
                return false
              }

              return true
            },
          })
        }

        insertMention(user: { id: string; displayName: string; profileId?: string }) {
          const range = this.quill.getSelection()
          if (!range) return

          const mentionData = {
            id: user.id,
            displayName: user.displayName,
            text: `@${user.displayName}`,
            profileId: user.profileId || user.id,
          }

          this.quill.insertEmbed(range.index, 'mention', mentionData)
          this.quill.insertText(range.index + 1, ' ')
          this.quill.setSelection(getCursorPosAfterMention(this.quill, range.index), 0, 'user')
        }
      }
      Quill.register('modules/mention', MentionModule)

      // ----- Hashtag Module -----
      class HashtagModule {
        private isFormatting = false
        constructor(private quill: any) {
          this.quill.on('text-change', this.handleTextChange.bind(this))
        }
        handleTextChange(_delta: any, _old: any, source: string) {
          if (source !== 'user' || this.isFormatting) return
          setTimeout(() => this.detectHashtags(), 0)
        }
        detectHashtags() {
          this.isFormatting = true
          try {
            const contents = this.quill.getContents()
            const total = this.quill.getLength()

            // Remove all existing hashtag formatting first
            this.quill.formatText(0, total, 'hashtag', false, 'silent')

            // Build a map of text positions, skipping embeds
            // This allows us to detect hashtags that span across embeds
            const textPositions: Array<{ char: string; quillIndex: number }> = []
            let quillIndex = 0

            for (const op of contents.ops || []) {
              if (typeof op.insert === 'string') {
                // Regular text - map each character
                for (let i = 0; i < op.insert.length; i++) {
                  textPositions.push({ char: op.insert[i], quillIndex })
                  quillIndex++
                }
              } else if (typeof op.insert === 'object' && op.insert) {
                // Embed (emoji, mention, etc.) - skip it in text positions but advance quillIndex
                // This ensures hashtags can span across embeds
                quillIndex++
              }
            }

            // Store the final quillIndex for calculating ranges at the end
            const finalQuillIndex = quillIndex

            const isZW = (ch: string) => ch === '\u200B' || ch === '\uFEFF'
            const isWord = (ch: string) => /[A-Za-z0-9_]/.test(ch)
            const isBoundary = (ch: string) => !/[A-Za-z0-9_#]/.test(ch)

            let textIdx = 0
            while (textIdx < textPositions.length) {
              const { char, quillIndex: startQuillIdx } = textPositions[textIdx]

              if (char === '#') {
                // Check if # is at start or preceded by boundary
                const prevChar = textIdx > 0 ? textPositions[textIdx - 1].char : ''
                if (textIdx === 0 || isBoundary(prevChar)) {
                  // Find the end of the hashtag in text positions
                  let endTextIdx = textIdx + 1
                  while (
                    endTextIdx < textPositions.length &&
                    (isWord(textPositions[endTextIdx].char) || isZW(textPositions[endTextIdx].char))
                  ) {
                    endTextIdx++
                  }

                  // Get the hashtag text (without #)
                  const hashtagText = textPositions
                    .slice(textIdx + 1, endTextIdx)
                    .map(p => p.char)
                    .join('')
                    .replace(/[\u200B\uFEFF]/g, '')

                  // Only format if hashtag has valid content
                  if (hashtagText.length > 0 && /^[A-Za-z0-9_]+$/.test(hashtagText)) {
                    // Calculate the end quill index (may include embeds between start and end)
                    const endQuillIdx =
                      endTextIdx < textPositions.length ? textPositions[endTextIdx].quillIndex : finalQuillIndex

                    // Format the hashtag, including any embeds in between
                    // Quill will apply the format to the range, skipping embeds automatically
                    this.quill.formatText(startQuillIdx, endQuillIdx - startQuillIdx, 'hashtag', hashtagText, 'silent')
                  }
                  textIdx = endTextIdx
                } else {
                  textIdx++
                }
              } else {
                textIdx++
              }
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error in hashtag detection:', e)
          } finally {
            this.isFormatting = false
          }
        }
      }
      Quill.register('modules/hashtag', HashtagModule)

      // ----- Custom Emoji (inline) Embed Blot -----
      class CustomEmojiBlot extends Embed {
        static blotName = 'customEmoji'
        static tagName = 'img'
        static className = 'custom-emoji-inline'
        static create(value: { src: string; alt?: string; id?: string; name?: string }) {
          const node = super.create() as HTMLImageElement
          node.setAttribute('src', value.src)
          if (value.alt) node.setAttribute('alt', value.alt)
          if (value.id) node.setAttribute('data-id', value.id)
          if (value.name) node.setAttribute('data-name', value.name)
          node.setAttribute('draggable', 'false')
          node.classList.add(CustomEmojiBlot.className)
          return node
        }
        static value(node: HTMLImageElement) {
          return {
            src: node.getAttribute('src') || '',
            alt: node.getAttribute('alt') || '',
            id: node.getAttribute('data-id') || '',
            name: node.getAttribute('data-name') || '',
          }
        }
      }
      Quill.register({ 'formats/customEmoji': CustomEmojiBlot })

      // ----- Code Block (block) Embed Blot -----
      class CodeBlockBlot extends Embed {
        static blotName = 'codeBlock'
        static tagName = 'div'
        static className = 'code-block'

        static create(value: { code: string; language: string }) {
          const node = super.create() as HTMLDivElement
          node.setAttribute('data-code', value.code)
          node.setAttribute('data-language', value.language)
          node.classList.add(CodeBlockBlot.className)

          // Make the node behave like a line break for text flow
          node.style.display = 'block'
          node.style.margin = '8px 0'
          node.style.borderRadius = '8px'

          // Prevent text selection and cursor placement
          node.style.userSelect = 'none'
          node.style.pointerEvents = 'auto'
          node.setAttribute('contenteditable', 'false')

          const lineNumbersContainer = document.createElement('div')
          lineNumbersContainer.style.cssText = `
            display: flex;
            margin: 0;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 13px;
            line-height: 1.5;
            background: #282c34;
          `

          // Add code content with highlight.js
          const codeContent = document.createElement('pre')
          codeContent.style.cssText = `
            margin: 0;
            padding: 8px;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 13px;
            line-height: 1.5;
            color: #abb2bf;
            overflow-x: auto;
            white-space: pre-wrap;
            word-wrap: break-word;
            background: #282c34;
            flex: 1;
          `

          const lineNumbers = document.createElement('div')
          lineNumbers.style.cssText = `
            padding: 8px 8px 8px 12px;
            background: #21252b;
            color: #5c6370;
            font-size: 13px;
            line-height: 1.5;
            text-align: right;
            user-select: none;
            border-right: 1px solid #444;
            min-width: 40px;
          `

          // Generate line numbers - ensure all lines are counted including the last one
          const lines = value.code.split('\n')
          // Count all lines including empty trailing line if code ends with newline
          const actualLineCount = lines.length

          // Clear any existing content
          lineNumbers.innerHTML = ''

          // Generate individual line number divs for each line
          for (let index = 0; index < actualLineCount; index++) {
            const lineNumberDiv = document.createElement('div')
            lineNumberDiv.style.cssText = `
              display: block;
              height: 19.5px;
              line-height: 1.5;
            `
            lineNumberDiv.textContent = String(index + 1)
            lineNumbers.appendChild(lineNumberDiv)
          }

          lineNumbersContainer.appendChild(lineNumbers)
          lineNumbersContainer.appendChild(codeContent)
          codeContent.textContent = value.code

          node.appendChild(lineNumbersContainer)

          // Apply syntax highlighting using highlight.js
          codeContent.className = 'hljs'
          codeContent.setAttribute('data-language', value.language)

          // Initially show plain text, then highlight with highlight.js
          codeContent.textContent = value.code

          // Use highlight.js for syntax highlighting
          setTimeout(async () => {
            try {
              const hljs = (await import('highlight.js')).default

              const languageMap: Record<string, string> = {
                csharp: 'csharp',
                cpp: 'cpp',
                powershell: 'powershell',
                dockerfile: 'dockerfile',
                plain: 'plaintext',
              }

              const hljsLanguage = languageMap[value.language] || value.language

              if (hljsLanguage && hljs.getLanguage(hljsLanguage)) {
                const highlighted = hljs.highlight(value.code, { language: hljsLanguage })
                codeContent.innerHTML = highlighted.value
              } else {
                // Fallback: apply basic highlighting if language not supported
                let highlightedCode = value.code
                if (value.language !== 'plain') {
                  highlightedCode = highlightedCode.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

                  if (value.language === 'javascript' || value.language === 'typescript') {
                    highlightedCode = highlightedCode.replace(
                      /\b(const|let|var|function|if|else|for|while|return|class|interface|import|export|async|await|try|catch|finally)\b/g,
                      '<span class="hljs-keyword">$1</span>'
                    )
                    highlightedCode = highlightedCode.replace(
                      /(["'`])((?:\\.|(?!\1)[^\\])*?)\1/g,
                      '<span class="hljs-string">$1$2$1</span>'
                    )
                    highlightedCode = highlightedCode.replace(
                      /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm,
                      '<span class="hljs-comment">$1</span>'
                    )
                    highlightedCode = highlightedCode.replace(/\b(\d+\.?\d*)\b/g, '<span class="hljs-number">$1</span>')
                  } else if (value.language === 'python') {
                    highlightedCode = highlightedCode.replace(
                      /\b(def|class|if|else|elif|for|while|import|from|return|True|False|None|try|except|finally)\b/g,
                      '<span class="hljs-keyword">$1</span>'
                    )
                    highlightedCode = highlightedCode.replace(
                      /(["'`])((?:\\.|(?!\1)[^\\])*?)\1/g,
                      '<span class="hljs-string">$1$2$1</span>'
                    )
                    highlightedCode = highlightedCode.replace(/(#.*$)/gm, '<span class="hljs-comment">$1</span>')
                    highlightedCode = highlightedCode.replace(/\b(\d+\.?\d*)\b/g, '<span class="hljs-number">$1</span>')
                  }
                  codeContent.innerHTML = highlightedCode
                }
              }
            } catch (error) {
              codeContent.textContent = value.code
            }
          }, 10)

          node.addEventListener('click', e => {
            e.preventDefault()
            e.stopPropagation()

            // Get the current code and language from the node's data attributes
            const currentCode = node.getAttribute('data-code') || value.code
            const currentLanguage = node.getAttribute('data-language') || value.language

            const event = new CustomEvent('quill-codeblock-edit', {
              detail: {
                quill: this.quill,
                code: currentCode,
                language: currentLanguage,
                node: node,
              },
            })
            window.dispatchEvent(event)
          })

          node.addEventListener('mousedown', e => {
            e.preventDefault()
            e.stopPropagation()

            // Determine if click is on left or right half of the block
            const rect = node.getBoundingClientRect()
            const clickX = e.clientX
            const blockCenterX = rect.left + rect.width / 2
            const isLeftHalf = clickX < blockCenterX

            // Find the Quill editor
            const editorContainer = node.closest('.ql-container')
            if (!editorContainer) return

            const quillInstance = (editorContainer as any).__quill
            if (!quillInstance) return

            // Find this code block's index by walking through all blocks in the editor
            const allBlocks: Element[] = []

            // Collect all blocks with their DOM order
            editorContainer.querySelectorAll('.code-block, .math-block').forEach((block: Element) => {
              allBlocks.push(block)
            })

            // Find this block's DOM index
            let domIndex = -1
            for (let i = 0; i < allBlocks.length; i++) {
              if (allBlocks[i] === node) {
                domIndex = i
                break
              }
            }

            if (domIndex === -1) return

            // Scan through Quill contents to find this block's actual index
            const contents = quillInstance.getContents()
            let currentIndex = 0
            let blockCount = 0

            for (const op of contents.ops || []) {
              if (typeof op.insert === 'object' && op.insert) {
                const isCodeBlock = (op.insert as any).codeBlock !== undefined
                const isMathBlock = (op.insert as any).math !== undefined

                if (isCodeBlock || isMathBlock) {
                  if (blockCount === domIndex) {
                    // Found our block
                    const cursorPos = isLeftHalf ? currentIndex : currentIndex + 1
                    setTimeout(() => {
                      quillInstance.setSelection(cursorPos, 0)
                      quillInstance.focus()
                    }, 10)
                    return
                  }
                  blockCount++
                }
              }
              const opLength = typeof op.insert === 'string' ? (op.insert as string).length : 1
              currentIndex += opLength
            }
          })

          node.addEventListener('keydown', e => {
            e.preventDefault()
            e.stopPropagation()
          })

          return node
        }

        static value(node: HTMLDivElement) {
          return {
            code: node.getAttribute('data-code') || '',
            language: node.getAttribute('data-language') || '',
          }
        }
      }
      Quill.register({ 'formats/codeBlock': CodeBlockBlot })

      // ----- Math (block) Embed Blot -----
      class MathBlot extends Embed {
        static blotName = 'math'
        static tagName = 'div'
        static className = 'math-block'

        static create(value: { latex: string; display?: boolean }) {
          const node = super.create() as HTMLDivElement
          node.setAttribute('data-latex', value.latex)
          node.setAttribute('data-display', String(value.display || true))
          node.classList.add(MathBlot.className)

          // Make the node behave like a line break for text flow
          node.style.display = 'block'
          node.style.margin = '8px 0'
          node.style.textAlign = 'center'

          // Prevent text selection and cursor placement
          node.style.userSelect = 'none'
          node.style.pointerEvents = 'auto'
          node.setAttribute('contenteditable', 'false')

          // Add click handler for editing
          node.addEventListener('click', e => {
            e.preventDefault()
            e.stopPropagation()

            // Get the current LaTeX from the node's data attribute
            const currentLatex = node.getAttribute('data-latex') || value.latex

            // Dispatch custom event for math edit
            const event = new CustomEvent('quill-math-edit', {
              detail: {
                quill: this.quill,
                latex: currentLatex,
                node: node,
              },
            })
            window.dispatchEvent(event)
          })

          // Handle mousedown to place cursor before or after block based on click position
          node.addEventListener('mousedown', e => {
            e.preventDefault()
            e.stopPropagation()

            // Determine if click is on left or right half of the block
            const rect = node.getBoundingClientRect()
            const clickX = e.clientX
            const blockCenterX = rect.left + rect.width / 2
            const isLeftHalf = clickX < blockCenterX

            // Find the Quill editor
            const editorContainer = node.closest('.ql-container')
            if (!editorContainer) return

            const quillInstance = (editorContainer as any).__quill
            if (!quillInstance) return

            // Find this block's index by walking through all blocks in the editor
            const allBlocks: Element[] = []
            editorContainer.querySelectorAll('.code-block, .math-block').forEach((block: Element) => {
              allBlocks.push(block)
            })

            // Find this block's DOM index
            let domIndex = -1
            for (let i = 0; i < allBlocks.length; i++) {
              if (allBlocks[i] === node) {
                domIndex = i
                break
              }
            }

            if (domIndex === -1) return

            // Scan through Quill contents to find this block's actual index
            const contents = quillInstance.getContents()
            let currentIndex = 0
            let blockCount = 0

            for (const op of contents.ops || []) {
              if (typeof op.insert === 'object' && op.insert) {
                const isCodeBlock = (op.insert as any).codeBlock !== undefined
                const isMathBlock = (op.insert as any).math !== undefined

                if (isCodeBlock || isMathBlock) {
                  if (blockCount === domIndex) {
                    // Found our block
                    const cursorPos = isLeftHalf ? currentIndex : currentIndex + 1
                    setTimeout(() => {
                      quillInstance.setSelection(cursorPos, 0)
                      quillInstance.focus()
                    }, 10)
                    return
                  }
                  blockCount++
                }
              }
              const opLength = typeof op.insert === 'string' ? (op.insert as string).length : 1
              currentIndex += opLength
            }
          })

          // Prevent keyboard navigation into math block
          node.addEventListener('keydown', e => {
            e.preventDefault()
            e.stopPropagation()
          })

          // Initially show raw LaTeX, will be rendered by KaTeX after mount
          node.innerHTML = `$${value.latex}$`

          // Use a small delay to ensure the DOM is ready
          setTimeout(async () => {
            try {
              const katex = (await import('katex')).default

              // Preprocess LaTeX to fix common issues
              const preprocessLatex = (latex: string) => {
                // Fix exponents without braces: x^111 -> x^{111}
                // This regex matches ^ followed by one or more digits that are not already in braces
                let processed = latex.replace(/\^(\d+)(?![^{])/g, '^{$1}')
                // Fix subscripts without braces: x_111 -> x_{111}
                processed = processed.replace(/_(\d+)(?![^{])/g, '_{$1}')
                // Fix mixed exponents and subscripts: x^111_222 -> x^{111}_{222}
                processed = processed.replace(/\^(\d+)_(\d+)/g, '^{$1}_{$2}')
                processed = processed.replace(/_(\d+)\^(\d+)/g, '_{$1}^{$2}')

                // Additional fixes for complex cases
                // Fix cases like e^{-x^7777} -> e^{-x^{7777}}
                processed = processed.replace(/([a-zA-Z])\^(\d+)/g, '$1^{$2}')

                return processed
              }

              // Handle multiple formulas separated by line breaks
              const formulas = value.latex.split('\n').filter(formula => formula.trim())

              if (formulas.length === 1) {
                // Single formula
                const processedLatex = preprocessLatex(value.latex.trim())
                const rendered = katex.renderToString(processedLatex, {
                  throwOnError: false,
                  displayMode: true,
                })
                node.innerHTML = rendered
                // eslint-disable-next-line no-console
                // console.log('Math block created and rendered:', value.latex)
              } else {
                // Multiple formulas - render each on a new line
                const renderedFormulas = formulas.map(formula => {
                  try {
                    const processedLatex = preprocessLatex(formula.trim())
                    return katex.renderToString(processedLatex, {
                      throwOnError: false,
                      displayMode: true,
                    })
                  } catch (error) {
                    return `<div class="math-error">Error: ${formula}</div>`
                  }
                })

                node.innerHTML = renderedFormulas.join('<br/>')
                // eslint-disable-next-line no-console
                // console.log('Multiple math blocks created and rendered:', value.latex)
              }
            } catch (error) {
              // Keep raw LaTeX if KaTeX fails
              // eslint-disable-next-line no-console
              console.error('Failed to render math block during creation:', value.latex, error)
            }
          }, 10)

          return node
        }

        static value(node: HTMLDivElement) {
          return {
            latex: node.getAttribute('data-latex') || '',
            display: node.getAttribute('data-display') === 'true',
          }
        }
      }
      Quill.register({ 'formats/math': MathBlot })

      // ----- Math Module -----
      class MathModule {
        constructor(private quill: any) {
          // Store Quill instance on container for access from blots
          if (this.quill.container) {
            ;(this.quill.container as any).__quill = this.quill
          }

          this.addMathButton()

          this.setupCursorHandling()

          // Re-render all existing math blocks on initialization
          setTimeout(() => {
            this.rerenderAllMathBlocks()
          }, 100)
        }

        addMathButton() {
          const toolbar = this.quill.getModule('toolbar')
          if (toolbar) {
            const mathButton = document.createElement('button')
            mathButton.innerHTML = '∑'
            mathButton.className = 'ql-math'
            mathButton.title = 'Insert Math'
            mathButton.addEventListener('click', () => {
              this.showMathDialog()
            })

            // Find the toolbar container and add the button
            const toolbarContainer = this.quill.container.previousElementSibling
            if (toolbarContainer && toolbarContainer.classList.contains('ql-toolbar')) {
              toolbarContainer.appendChild(mathButton)
            }
          }
        }

        setupCursorHandling() {
          // Helper to find all block positions from Quill contents
          const findAllBlocks = () => {
            const contents = this.quill.getContents()
            const blocks: Array<{ index: number; type: string }> = []
            let currentIndex = 0

            for (const op of contents.ops || []) {
              if (typeof op.insert === 'object' && op.insert) {
                const isCodeBlock = (op.insert as any).codeBlock !== undefined
                const isMathBlock = (op.insert as any).math !== undefined

                if (isCodeBlock || isMathBlock) {
                  blocks.push({
                    index: currentIndex,
                    type: isCodeBlock ? 'code' : 'math',
                  })
                }
              }
              const opLength = typeof op.insert === 'string' ? (op.insert as string).length : 1
              currentIndex += opLength
            }
            return blocks
          }

          // Helper to find block at or near cursor position
          const findBlockNearCursor = (cursorPos: number, direction: 'left' | 'right') => {
            const blocks = findAllBlocks()

            if (direction === 'left') {
              // Find block immediately before cursor (cursor at index + 1)
              for (const block of blocks) {
                if (block.index + 1 === cursorPos) {
                  return block
                }
              }
            } else {
              // Find block at cursor position
              for (const block of blocks) {
                if (block.index === cursorPos) {
                  return block
                }
              }
            }
            return null
          }

          // Handle arrow keys to navigate around blocks
          this.quill.root.addEventListener(
            'keydown',
            (e: KeyboardEvent) => {
              const selection = this.quill.getSelection()
              if (!selection) return

              const cursorPos = selection.index

              // Handle Enter key - allow default behavior
              if (e.key === 'Enter') {
                return
              }

              // Handle ArrowLeft
              if (e.key === 'ArrowLeft') {
                const block = findBlockNearCursor(cursorPos, 'left')
                if (block) {
                  // Cursor is right after a block, move to before it
                  e.preventDefault()
                  this.quill.setSelection(block.index, 0)
                  return
                }
              }

              // Handle ArrowRight
              if (e.key === 'ArrowRight') {
                const block = findBlockNearCursor(cursorPos, 'right')
                if (block) {
                  // Cursor is at a block, move to after it
                  e.preventDefault()
                  this.quill.setSelection(block.index + 1, 0)
                  return
                }
              }

              // Handle ArrowUp and ArrowDown
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                const blocks = findAllBlocks()

                // Check if cursor is at any block
                for (const block of blocks) {
                  if (block.index === cursorPos) {
                    e.preventDefault()
                    if (e.key === 'ArrowUp') {
                      this.quill.setSelection(Math.max(0, block.index - 1), 0)
                    } else {
                      this.quill.setSelection(block.index + 1, 0)
                    }
                    return
                  }
                }

                // Check if the next position would land on a block
                const nextPos = e.key === 'ArrowDown' ? cursorPos + 1 : cursorPos - 1
                for (const block of blocks) {
                  if (block.index === nextPos) {
                    e.preventDefault()
                    if (e.key === 'ArrowUp') {
                      this.quill.setSelection(block.index, 0)
                    } else {
                      this.quill.setSelection(block.index + 1, 0)
                    }
                    return
                  }
                }
              }
            },
            true
          )
        }

        showMathDialog() {
          // This will be handled by the parent component
          // We'll dispatch a custom event that the parent can listen to
          const event = new CustomEvent('quill-math-dialog', {
            detail: { quill: this.quill },
          })
          window.dispatchEvent(event)
        }

        insertMath(latex: string, display = true) {
          const range = this.quill.getSelection(true)
          let index = range ? range.index : this.quill.getLength()

          // Always add newline before the math block
          this.quill.insertText(index, '\n')
          index += 1

          this.quill.insertEmbed(index, 'math', {
            latex,
            display,
          })

          // Always add newline after the math block
          const blockEnd = index + 1
          this.quill.insertText(blockEnd, '\n')

          // Update Quill and set cursor
          this.quill.update()

          // Set cursor to the new line after math with a small delay to ensure math is rendered
          setTimeout(() => {
            const finalPos = blockEnd + 1
            this.quill.setSelection(finalPos)
            this.quill.focus()
          }, 100)
        }

        // Helper method to ensure proper text flow around math blocks
        ensureTextFlow() {
          const range = this.quill.getSelection()
          if (range) {
            const [block] = this.quill.getLine(range.index)
            if (block && block.domNode && block.domNode.classList.contains('math-block')) {
              // Only move cursor if it's inside the math block
              const blockStart = range.index
              const blockEnd = range.index + block.length
              const cursorPos = range.index

              if (cursorPos > blockStart && cursorPos < blockEnd) {
                // Move cursor to after the math block
                this.quill.setSelection(blockEnd)
              }
            }
          }
        }

        updateMath(node: HTMLElement, newLatex: string, callback?: () => void) {
          node.setAttribute('data-latex', newLatex)

          // Re-render the math with new LaTeX
          this.renderMathNode(node, newLatex, callback)
        }

        async renderMathNode(node: HTMLElement, latex: string, callback?: () => void) {
          try {
            const katex = (await import('katex')).default

            // Preprocess LaTeX to fix common issues
            const preprocessLatex = (latex: string) => {
              // Fix exponents without braces: x^111 -> x^{111}
              // This regex matches ^ followed by one or more digits that are not already in braces
              let processed = latex.replace(/\^(\d+)(?![^{])/g, '^{$1}')
              // Fix subscripts without braces: x_111 -> x_{111}
              processed = processed.replace(/_(\d+)(?![^{])/g, '_{$1}')
              // Fix mixed exponents and subscripts: x^111_222 -> x^{111}_{222}
              processed = processed.replace(/\^(\d+)_(\d+)/g, '^{$1}_{$2}')
              processed = processed.replace(/_(\d+)\^(\d+)/g, '_{$1}^{$2}')

              // Additional fixes for complex cases
              // Fix cases like e^{-x^7777} -> e^{-x^{7777}}
              processed = processed.replace(/([a-zA-Z])\^(\d+)/g, '$1^{$2}')

              return processed
            }

            // Handle multiple formulas separated by line breaks
            const formulas = latex.split('\n').filter(formula => formula.trim())

            let renderedHTML = ''
            if (formulas.length === 1) {
              // Single formula
              const processedLatex = preprocessLatex(latex.trim())
              renderedHTML = katex.renderToString(processedLatex, {
                throwOnError: false,
                displayMode: true,
              })
            } else {
              // Multiple formulas - render each on a new line
              const renderedFormulas = formulas.map(formula => {
                try {
                  const processedLatex = preprocessLatex(formula.trim())
                  return katex.renderToString(processedLatex, {
                    throwOnError: false,
                    displayMode: true,
                  })
                } catch (error) {
                  return `<div class="math-error">Error: ${formula}</div>`
                }
              })

              renderedHTML = renderedFormulas.join('<br/>')
            }

            // Update the node content
            node.innerHTML = renderedHTML

            // eslint-disable-next-line no-console
            console.log('Rendered math block:', latex, '->', renderedHTML.substring(0, 100) + '...')

            // Force Quill to update its content
            this.quill.update()

            // Call the callback after rendering is complete
            if (callback) {
              setTimeout(callback, 50) // Small delay to ensure DOM is updated
            }
          } catch (error) {
            // Fallback to raw LaTeX if KaTeX fails
            node.innerHTML = `$${latex}$`
            // eslint-disable-next-line no-console
            console.error('Failed to render math:', latex, error)

            // Force Quill to update its content
            this.quill.update()

            // Call the callback even if there's an error
            if (callback) {
              setTimeout(callback, 50)
            }
          }
        }

        async rerenderAllMathBlocks() {
          const mathBlocks = this.quill.container.querySelectorAll('.math-block')
          const renderPromises = []

          // eslint-disable-next-line no-console
          // console.log('Found math blocks to check:', mathBlocks.length)

          for (const block of mathBlocks) {
            const latex = block.getAttribute('data-latex')
            if (latex && block instanceof HTMLElement) {
              const innerHTML = block.innerHTML
              const needsRendering = innerHTML.includes('$') && !innerHTML.includes('katex')

              if (needsRendering) {
                renderPromises.push(this.renderMathNode(block, latex))
              } else {
                // eslint-disable-next-line no-console
                console.log('Math block already rendered, skipping:', latex)
              }
            }
          }

          await Promise.all(renderPromises)
          // eslint-disable-next-line no-console
          console.log('Math rendering completed')
        }

        rerenderMathBlocks() {
          setTimeout(() => {
            this.rerenderAllMathBlocks()
          }, 50)
        }

        async forceRerenderAllMathBlocks() {
          const mathBlocks = this.quill.container.querySelectorAll('.math-block')
          const renderPromises = []

          for (const block of mathBlocks) {
            const latex = block.getAttribute('data-latex')
            if (latex && block instanceof HTMLElement) {
              renderPromises.push(this.renderMathNode(block, latex))
            }
          }

          await Promise.all(renderPromises)
        }
      }
      Quill.register('modules/math', MathModule)

      // ----- Code Block Module -----
      class CodeBlockModule {
        constructor(private quill: any) {
          // Store Quill instance on container for access from blots
          if (this.quill.container) {
            ;(this.quill.container as any).__quill = this.quill
          }

          this.addCodeBlockButton()

          this.setupCursorHandling()

          // Normalize spacing before existing code blocks when editor initializes
          setTimeout(() => {
            try {
              this.normalizePrecedingNewlinesForExistingBlocks()
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('Normalize code-block spacing failed:', e)
            }
          }, 50)
        }

        addCodeBlockButton() {
          const toolbar = this.quill.getModule('toolbar')
          if (toolbar) {
            const codeBlockButton = document.createElement('button')
            codeBlockButton.innerHTML = 'C'
            codeBlockButton.className = 'ql-codeblock'
            codeBlockButton.title = 'Insert Code Block'
            codeBlockButton.addEventListener('click', () => {
              this.showCodeBlockDialog()
            })

            const toolbarContainer = this.quill.container.previousElementSibling
            if (toolbarContainer && toolbarContainer.classList.contains('ql-toolbar')) {
              toolbarContainer.appendChild(codeBlockButton)
            }
          }
        }

        setupCursorHandling() {
          // Helper to find all block positions from Quill contents
          const findAllBlocks = () => {
            const contents = this.quill.getContents()
            const blocks: Array<{ index: number; type: string }> = []
            let currentIndex = 0

            for (const op of contents.ops || []) {
              if (typeof op.insert === 'object' && op.insert) {
                const isCodeBlock = (op.insert as any).codeBlock !== undefined
                const isMathBlock = (op.insert as any).math !== undefined

                if (isCodeBlock || isMathBlock) {
                  blocks.push({
                    index: currentIndex,
                    type: isCodeBlock ? 'code' : 'math',
                  })
                }
              }
              const opLength = typeof op.insert === 'string' ? (op.insert as string).length : 1
              currentIndex += opLength
            }
            return blocks
          }

          // Helper to find block at or near cursor position
          const findBlockNearCursor = (cursorPos: number, direction: 'left' | 'right') => {
            const blocks = findAllBlocks()

            if (direction === 'left') {
              // Find block immediately before cursor (cursor at index + 1)
              for (const block of blocks) {
                if (block.index + 1 === cursorPos) {
                  return block
                }
              }
            } else {
              // Find block at cursor position
              for (const block of blocks) {
                if (block.index === cursorPos) {
                  return block
                }
              }
            }
            return null
          }

          // Handle arrow keys to navigate around blocks
          this.quill.root.addEventListener(
            'keydown',
            (e: KeyboardEvent) => {
              const selection = this.quill.getSelection()
              if (!selection) return

              const cursorPos = selection.index

              // Handle Enter key - allow default behavior
              if (e.key === 'Enter') {
                return
              }

              // Handle ArrowLeft
              if (e.key === 'ArrowLeft') {
                const block = findBlockNearCursor(cursorPos, 'left')
                if (block) {
                  // Cursor is right after a block, move to before it
                  e.preventDefault()
                  this.quill.setSelection(block.index, 0)
                  return
                }
              }

              // Handle ArrowRight
              if (e.key === 'ArrowRight') {
                const block = findBlockNearCursor(cursorPos, 'right')
                if (block) {
                  // Cursor is at a block, move to after it
                  e.preventDefault()
                  this.quill.setSelection(block.index + 1, 0)
                  return
                }
              }

              // Handle ArrowUp and ArrowDown
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                const blocks = findAllBlocks()

                // Check if cursor is at any block
                for (const block of blocks) {
                  if (block.index === cursorPos) {
                    e.preventDefault()
                    if (e.key === 'ArrowUp') {
                      this.quill.setSelection(Math.max(0, block.index - 1), 0)
                    } else {
                      this.quill.setSelection(block.index + 1, 0)
                    }
                    return
                  }
                }

                // Check if the next position would land on a block
                const nextPos = e.key === 'ArrowDown' ? cursorPos + 1 : cursorPos - 1
                for (const block of blocks) {
                  if (block.index === nextPos) {
                    e.preventDefault()
                    if (e.key === 'ArrowUp') {
                      this.quill.setSelection(block.index, 0)
                    } else {
                      this.quill.setSelection(block.index + 1, 0)
                    }
                    return
                  }
                }
              }
            },
            true
          )
        }

        showCodeBlockDialog() {
          const event = new CustomEvent('quill-codeblock-dialog', {
            detail: { quill: this.quill },
          })
          window.dispatchEvent(event)
        }

        // Ensure there is at most one newline (no blank line) immediately before each code block in existing content
        normalizePrecedingNewlinesForExistingBlocks() {
          const contents = this.quill.getContents()
          let cursorIndex = 0

          for (const op of contents.ops || []) {
            const isEmbed = typeof op.insert === 'object' && op.insert && (op.insert as any).codeBlock
            const opLength = typeof op.insert === 'string' ? (op.insert as string).length : 1

            if (isEmbed) {
              // Look back up to 200 chars and collapse trailing whitespace/newlines to at most one newline
              const lookbehind = Math.min(200, cursorIndex)
              if (lookbehind > 0) {
                const before = this.quill.getText(cursorIndex - lookbehind, lookbehind)
                const match = before.match(/[\s\n]+$/)
                if (match) {
                  const trailingLen = match[0].length
                  const keep = before.endsWith('\n') ? 1 : 0
                  const toDelete = trailingLen - keep
                  if (toDelete > 0) {
                    this.quill.deleteText(cursorIndex - trailingLen, toDelete, 'silent')
                    cursorIndex -= toDelete
                  }
                }
              }
            }

            cursorIndex += opLength
          }

          // Ensure editor renders the changes
          this.quill.update('silent')
        }

        insertCodeBlock(code: string, language: string) {
          const range = this.quill.getSelection(true)
          let index = range ? range.index : this.quill.getLength()

          // Always add newline before the code block
          this.quill.insertText(index, '\n')
          index += 1

          this.quill.insertEmbed(index, 'codeBlock', {
            code,
            language,
          })

          // Always add newline after the code block
          const blockEnd = index + 1
          this.quill.insertText(blockEnd, '\n')

          // Update Quill and set cursor
          this.quill.update()

          setTimeout(() => {
            const finalPos = blockEnd + 1
            this.quill.setSelection(finalPos)
            this.quill.focus()
          }, 100)
        }

        updateCodeBlock(node: HTMLElement, newCode: string, newLanguage: string, callback?: () => void) {
          // Update data attributes
          node.setAttribute('data-code', newCode)
          node.setAttribute('data-language', newLanguage)

          // Find the line numbers container (first child div with flex display)
          const lineNumbersContainer = node.querySelector('div[style*="display: flex"]') as HTMLElement

          if (lineNumbersContainer) {
            // Update line numbers (first child of lineNumbersContainer with background #21252b)
            const lineNumbers = lineNumbersContainer.querySelector('div[style*="background: #21252b"]') as HTMLElement
            if (lineNumbers) {
              const lines = newCode.split('\n')
              const actualLineCount = lines.length

              // Clear and regenerate line numbers as individual divs
              lineNumbers.innerHTML = ''
              for (let index = 0; index < actualLineCount; index++) {
                const lineNumberDiv = document.createElement('div')
                lineNumberDiv.style.cssText = `
                  display: block;
                  height: 19.5px;
                  line-height: 1.5;
                `
                lineNumberDiv.textContent = String(index + 1)
                lineNumbers.appendChild(lineNumberDiv)
              }
            }

            // Update code content (pre element)
            const codeContent = lineNumbersContainer.querySelector('pre') as HTMLPreElement
            if (codeContent) {
              codeContent.className = 'hljs'
              codeContent.setAttribute('data-language', newLanguage)

              // Initially show plain text
              codeContent.textContent = newCode

              // Use highlight.js for syntax highlighting
              setTimeout(async () => {
                try {
                  const hljs = (await import('highlight.js')).default

                  // Language mapping for highlight.js
                  const languageMap: Record<string, string> = {
                    csharp: 'csharp',
                    cpp: 'cpp',
                    powershell: 'powershell',
                    dockerfile: 'dockerfile',
                    plain: 'plaintext',
                  }

                  const hljsLanguage = languageMap[newLanguage] || newLanguage

                  if (hljsLanguage && hljs.getLanguage(hljsLanguage)) {
                    const highlighted = hljs.highlight(newCode, { language: hljsLanguage })
                    codeContent.innerHTML = highlighted.value
                  } else {
                    // Fallback: apply basic highlighting if language not supported
                    let highlightedCode = newCode
                    if (newLanguage !== 'plain') {
                      highlightedCode = highlightedCode
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')

                      if (newLanguage === 'javascript' || newLanguage === 'typescript') {
                        highlightedCode = highlightedCode.replace(
                          /\b(const|let|var|function|if|else|for|while|return|class|interface|import|export|async|await|try|catch|finally)\b/g,
                          '<span class="hljs-keyword">$1</span>'
                        )
                        highlightedCode = highlightedCode.replace(
                          /(["'`])((?:\\.|(?!\1)[^\\])*?)\1/g,
                          '<span class="hljs-string">$1$2$1</span>'
                        )
                        highlightedCode = highlightedCode.replace(
                          /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm,
                          '<span class="hljs-comment">$1</span>'
                        )
                        highlightedCode = highlightedCode.replace(
                          /\b(\d+\.?\d*)\b/g,
                          '<span class="hljs-number">$1</span>'
                        )
                      } else if (newLanguage === 'python') {
                        highlightedCode = highlightedCode.replace(
                          /\b(def|class|if|else|elif|for|while|import|from|return|True|False|None|try|except|finally)\b/g,
                          '<span class="hljs-keyword">$1</span>'
                        )
                        highlightedCode = highlightedCode.replace(
                          /(["'`])((?:\\.|(?!\1)[^\\])*?)\1/g,
                          '<span class="hljs-string">$1$2$1</span>'
                        )
                        highlightedCode = highlightedCode.replace(/(#.*$)/gm, '<span class="hljs-comment">$1</span>')
                        highlightedCode = highlightedCode.replace(
                          /\b(\d+\.?\d*)\b/g,
                          '<span class="hljs-number">$1</span>'
                        )
                      }
                      codeContent.innerHTML = highlightedCode
                    } else {
                      codeContent.textContent = newCode
                    }
                  }
                } catch (error) {
                  // Fallback to plain text if highlight.js fails
                  codeContent.textContent = newCode
                }
              }, 10)
            }
          }

          // Force Quill to update its internal representation
          this.quill.update('silent')

          if (callback) {
            setTimeout(callback, 50)
          }
        }
      }
      Quill.register('modules/codeBlock', CodeBlockModule)

      if (!mounted) return
      setReactQuillComp(() => ReactQuillMod.default)
    })()

    return () => {
      mounted = false
    }
  }, [])

  if (!ReactQuillComp) return null // or a small skeleton

  const ReactQuill = ReactQuillComp
  return <ReactQuill ref={ref as any} {...props} />
})

QuillWrapper.displayName = 'QuillWrapper'
export default QuillWrapper
